// noinspection DuplicatedCode

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { downloadExcel } from "react-export-table-to-excel";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { Card } from "@material-tailwind/react";

import { alertService } from "../../../services/AlertService";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { SearchBarSmallV2 } from "../../../components/form/SearchBar";
import { useForm } from "../../../components/isRequired";
import Skeltons from "../../../components/loading/Skeltons";
import ModalForm from "../../../components/modal/form/ModalForm";

import { getUser, insertUser, updateUser, userAdminSlice } from "./userSlice";
import { kirimResetPassword } from "../../hrd/transaksi/job-request/jobRequestTransaksiSlice";

import { Colors } from "../../../assets/Colors";

const PAGE_SIZE = 10;

const EXPORT_HEADER = [
  "Karyawan",
  "Username",
  "Bisnis Unit",
  "Jabatan",
  "Bagian",
  "Status",
];

const statusLabel = (s) => (s?.toString() === "1" ? "Aktif" : "NonAktif");

const UserPage = ({}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector(userAdminSlice);

  const initialState = {
    BisnisUnitID: "ALL",
    JabatanID: "ALL",
    BagianID: "ALL",
  };
  const { values, errors, touched, customHandler, multiFieldHandler } = useForm(
    initialState,
    []
  );

  // Filter yang sudah diterapkan lewat tombol "Terapkan".
  const [applied, setApplied] = useState(initialState);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [showModalAdd, setShowModalAdd] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [currentData, setCurrentData] = useState({});

  // Ambil SEMUA user sekali (start/count kosong = tanpa LIMIT di backend);
  // filter, pencarian, paginasi, dan export dikerjakan client-side.
  const muatData = useCallback(() => {
    dispatch(getUser({ start: "", count: "", search: "" }));
  }, [dispatch]);

  useEffect(() => {
    muatData();
  }, [muatData]);

  const allRows = useMemo(
    () => (Array.isArray(user.data) ? user.data : []),
    [user.data]
  );

  // ── Filter + pencarian (client-side) ───────────────────────
  const filtered = useMemo(() => {
    let rows = [...allRows];

    if (applied.BisnisUnitID && applied.BisnisUnitID !== "ALL") {
      rows = rows.filter((r) => r.BisnisUnitID === applied.BisnisUnitID);
    }
    if (applied.JabatanID && applied.JabatanID !== "ALL") {
      rows = rows.filter((r) => r.JabatanID === applied.JabatanID);
    }
    if (applied.BagianID && applied.BagianID !== "ALL") {
      rows = rows.filter((r) => r.BagianID === applied.BagianID);
    }

    const kw = searchQuery.trim().toLowerCase();
    if (kw) {
      rows = rows.filter((r) =>
        [
          r.NamaKaryawan,
          r.Username,
          r.NamaBisnisUnit,
          r.NamaJabatan,
          r.NamaBagian,
        ].some((v) => v && v.toString().toLowerCase().includes(kw))
      );
    }

    rows.sort((a, b) => (a.Username || "").localeCompare(b.Username || ""));
    return rows;
  }, [allRows, applied, searchQuery]);

  // ── Paginasi ───────────────────────────────────────────────
  const totalPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPage);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  useEffect(() => {
    setPage(1);
  }, [applied, searchQuery]);

  // ── Handler ────────────────────────────────────────────────
  const handleSearchChange = useCallback((e) => {
    const val = typeof e === "string" ? e : e?.target?.value || "";
    setSearchInput(val);
    setSearchQuery(val);
  }, []);

  const terapkanFilter = () => {
    setApplied({
      BisnisUnitID: values.BisnisUnitID,
      JabatanID: values.JabatanID,
      BagianID: values.BagianID,
    });
  };

  const resetFilter = () => {
    // multiFieldHandler, bukan customHandler 3×: customHandler menyusun nilai baru
    // dari `values` hasil closure, jadi tiga panggilan beruntun saling menimpa dan
    // hanya yang terakhir bertahan.
    multiFieldHandler({
      BisnisUnitID: "ALL",
      JabatanID: "ALL",
      BagianID: "ALL",
    });
    setApplied(initialState);
    setSearchInput("");
    setSearchQuery("");
  };

  function handleInsertUser(param) {
    dispatch(
      insertUser({ Username: param.inputOneValue, Nik: param.dropdownUserAdmin })
    ).then(() => {
      alertService.success("Berhasil menambah user", {
        autoClose: true,
        keepAfterRouteChange: false,
      });
      muatData();
    });
  }

  function handleUpdateUser(param) {
    dispatch(
      updateUser({
        NamaUser: param.inputOneValue,
        UserID: currentData.Username,
        status: param.status,
      })
    ).then(() => {
      alertService.success("Berhasil mengubah user", {
        autoClose: true,
        keepAfterRouteChange: false,
      });
      muatData();
    });
  }

  const handleResetPassword = (username) => {
    if (!window.confirm(`Kirim reset password untuk user "${username}"?`)) return;
    dispatch(kirimResetPassword({ Username: username })).then((response) => {
      if (response?.payload?.status === "Success") {
        toast.success("Reset password terkirim", {
          position: "top-center",
          autoClose: 1500,
          hideProgressBar: true,
          theme: "light",
        });
      } else {
        toast.error("Gagal mengirim reset password", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: true,
          theme: "light",
        });
      }
    });
  };

  // ── Export ─────────────────────────────────────────────────
  // Isi export = seluruh data yang SEDANG terfilter, bukan hanya halaman aktif.
  const exportBody = () =>
    filtered.map((r) => [
      r.NamaKaryawan || "",
      r.Username || "",
      r.NamaBisnisUnit || "",
      r.NamaJabatan || "",
      r.NamaBagian || "",
      statusLabel(r.Status),
    ]);

  const handleDownloadExcel = () => {
    downloadExcel({
      fileName: "Master_User",
      sheet: "data",
      tablePayload: { header: EXPORT_HEADER, body: exportBody() },
    });
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(13);
    doc.text("Master User", 14, 14);
    doc.setFontSize(9);
    doc.text(`${filtered.length} user — dicetak ${new Date().toLocaleDateString("id-ID")}`, 14, 19);
    autoTable(doc, {
      head: [EXPORT_HEADER],
      body: exportBody(),
      startY: 24,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: "bold" },
    });
    doc.save("Master_User.pdf");
  };

  const colHeader = [
    "Karyawan",
    "Username",
    "Bisnis Unit",
    "Jabatan",
    "Bagian",
    "Status",
    "Actions",
  ];

  return (
    <div className="md:ml-64">
      <div
        style={{ background: Colors.primary }}
        className="pt-14 pb-16 px-3 md:px-8 h-auto"
      >
        <div className="container mx-auto max-w-full" />
      </div>

      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 px-4">
            <Card>
              {/* ── Baris filter ── */}
              <div className="flex flex-row flex-wrap items-start justify-start w-full px-4 py-3 gap-4">
                <div className="w-full sm:w-1/5">
                  <HrdInputGenerator
                    type={"BisnisUnit"}
                    name={"BisnisUnitID"}
                    title={"Bisnis Unit"}
                    value={values}
                    includeAll={true}
                    onChange={customHandler}
                    touched={touched}
                    errors={errors}
                    size={"full"}
                  />
                </div>
                <div className="w-full sm:w-1/5">
                  <HrdInputGenerator
                    type={"Jabatan"}
                    name={"JabatanID"}
                    title={"Jabatan"}
                    value={values}
                    includeAll={true}
                    onChange={customHandler}
                    touched={touched}
                    errors={errors}
                    size={"full"}
                  />
                </div>
                <div className="w-full sm:w-1/5">
                  <HrdInputGenerator
                    type={"Bagian"}
                    name={"BagianID"}
                    title={"Bagian"}
                    value={values}
                    includeAll={true}
                    onChange={customHandler}
                    touched={touched}
                    errors={errors}
                    size={"full"}
                  />
                </div>
                <div>
                  <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                    &nbsp;
                  </label>
                  <div className="flex flex-row gap-2">
                    <button
                      type="button"
                      onClick={terapkanFilter}
                      className="h-[42px] text-main-100 hover:text-white border border-main-100 hover:bg-main-100 font-medium rounded-lg text-sm px-7 text-center"
                    >
                      Terapkan
                    </button>
                    <button
                      type="button"
                      onClick={resetFilter}
                      className="h-[42px] text-gray-600 hover:text-white border border-gray-400 hover:bg-gray-400 font-medium rounded-lg text-sm px-5 text-center"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              {user.isGetFetching ? (
                <div className="w-full pt-0">
                  <Skeltons />
                </div>
              ) : (
                <>
                  {/* ── Toolbar: Tambah / Export / Pencarian ── */}
                  <div className="flex w-full justify-between px-4 pt-2 flex-col sm:flex-row mt-2 gap-2">
                    <div className="flex flex-row gap-2 flex-wrap">
                      <button
                        onClick={() => setShowModalAdd(true)}
                        className="flex items-center justify-center my-1 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-main-100 hover:bg-main-100 hover:text-white rounded shadow"
                      >
                        Tambah +
                      </button>
                      <button
                        onClick={handleDownloadExcel}
                        className="flex items-center justify-center my-1 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-green-600 hover:bg-green-600 hover:text-white rounded shadow"
                      >
                        Download Excel
                      </button>
                      <button
                        onClick={handleDownloadPDF}
                        className="flex items-center justify-center my-1 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-red-400 hover:bg-red-400 hover:text-white rounded shadow"
                      >
                        Download PDF
                      </button>
                    </div>
                    <div className="flex flex-row w-96">
                      <SearchBarSmallV2
                        text={"Cari karyawan / username / jabatan"}
                        value={searchInput}
                        funcs={handleSearchChange}
                      />
                    </div>
                  </div>

                  {/* ── Tabel ── */}
                  <div className="overflow-x-auto px-4 pt-2">
                    <table className="table-auto w-full text-sm text-left text-gray-500 mt-4">
                      <thead className="text-xs text-orange-600 uppercase bg-gray-50">
                        <tr>
                          {colHeader.map((h, i) => (
                            <th key={i} scope="col" className="py-3 px-6">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={colHeader.length}
                              className="text-center py-8 text-gray-400"
                            >
                              {searchQuery
                                ? `Tidak ada data untuk "${searchQuery}"`
                                : "Tidak ada data"}
                            </td>
                          </tr>
                        ) : (
                          pageRows.map((row, id) => (
                            <tr
                              key={row.Username || id}
                              className={
                                id % 2 === 0
                                  ? "bg-gray-50 border-b"
                                  : "bg-white border-b"
                              }
                            >
                              <td className="py-4 px-6">{row.NamaKaryawan || "-"}</td>
                              <td className="py-4 px-6 font-medium text-gray-800">
                                {row.Username || "-"}
                              </td>
                              <td className="py-4 px-6">{row.NamaBisnisUnit || "-"}</td>
                              <td className="py-4 px-6">{row.NamaJabatan || "-"}</td>
                              <td className="py-4 px-6">{row.NamaBagian || "-"}</td>
                              <td className="py-4 px-6">
                                <span
                                  className={
                                    row.Status?.toString() === "1"
                                      ? "text-green-600 font-medium"
                                      : "text-red-600 font-medium"
                                  }
                                >
                                  {statusLabel(row.Status)}
                                </span>
                              </td>
                              <td className="py-4 px-6 whitespace-nowrap">
                                <div className="flex flex-row gap-2">
                                  <button
                                    onClick={() => {
                                      setCurrentData(row);
                                      setShowModalEdit(true);
                                    }}
                                    className="border text-white bg-main-100 hover:bg-white hover:border-main-100 hover:text-main-100 font-medium rounded-lg text-sm px-4 py-2"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() =>
                                      navigate("/setup-otoritas/" + row.Username)
                                    }
                                    className="bg-white hover:bg-gray-100 text-gray-800 font-medium py-2 px-4 border border-gray-400 rounded-lg text-sm"
                                  >
                                    Otoritas
                                  </button>
                                  <button
                                    onClick={() => handleResetPassword(row.Username)}
                                    className="bg-white hover:bg-gray-100 text-gray-800 font-medium py-2 px-4 border border-gray-400 rounded-lg text-sm"
                                  >
                                    Reset Password
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* ── Info + Paginasi ── */}
                  <div className="flex flex-row justify-between items-center gap-4 px-4 py-4 text-sm text-gray-600">
                    <span>
                      {filtered.length} user
                      {filtered.length !== allRows.length
                        ? ` (dari ${allRows.length})`
                        : ""}
                    </span>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={safePage <= 1}
                        className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                      >
                        Prev
                      </button>
                      <span>
                        Page {safePage} / {totalPage}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
                        disabled={safePage >= totalPage}
                        className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>

      {showModalAdd && (
        <ModalForm
          showModal={showModalAdd}
          addEnabled={true}
          modal={(status) => {
            setShowModalAdd(status.showModalAdd);
            setShowModalEdit(status.showModalEdit);
          }}
          modalTitle="Tambah User"
          dropdownUser={true}
          inputOneTitle="Username"
          inputOneValue=""
          warningalert={true}
          submitButtonTitle="Submit"
          handleSubmit={handleInsertUser}
        />
      )}
      {showModalEdit && (
        <ModalForm
          showModal={showModalEdit}
          editEnabled={true}
          id={currentData.Username}
          modal={(status) => {
            setShowModalAdd(status.showModalAdd);
            setShowModalEdit(status.showModalEdit);
          }}
          modalTitle="Ubah User"
          inputOneTitle="Nama User"
          inputOneValue={currentData.NamaKaryawan}
          status={currentData.Status}
          submitButtonTitle="Submit"
          handleSubmit={handleUpdateUser}
        />
      )}
    </div>
  );
};

export default UserPage;
